import React, { useEffect, useRef, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  GitBranch, Plus, Trash2, Play, ChevronDown, ChevronUp, History,
  Download, Upload, Save, Tag, RefreshCw, ArrowRight, X, Info,
  CheckCircle, XCircle, Clock, Code2, Copy, Edit2
} from "lucide-react";

const genId = () => Math.random().toString(36).substr(2, 9);

const METHOD_COLORS = { GET: "#059669", POST: "#2563EB", PUT: "#D97706", PATCH: "#7C3AED", DELETE: "#DC2626" };
const OVERRIDE_MODES = [
  { value: "reference", label: "Reference", desc: "Use API definition as-is, no local changes" },
  { value: "delta", label: "Override (Delta)", desc: "Override payload and/or headers only" },
  { value: "fork", label: "Fork (Full Copy)", desc: "Embed a full copy of the API def in this step — independent of the original" },
];

// ── Step Config Panel ────────────────────────────────────────────────
function StepConfigPanel({ step, apiDefs, onUpdate }) {
  if (!step) return (
    <div className="flex items-center justify-center h-full text-gray-400 text-sm">
      Select a step to configure it
    </div>
  );

  const set = (key, val) => onUpdate({ ...step, [key]: val });

  const selectedApiDef = apiDefs.find(d => d._id === step.api_definition_id);

  const addHeaderInjection = () => {
    set("header_injections", [...(step.header_injections || []), { from_field: "", to_header: "Authorization", format: "Bearer {value}" }]);
  };

  const updateInjection = (idx, updates) => {
    const inj = [...(step.header_injections || [])];
    inj[idx] = { ...inj[idx], ...updates };
    set("header_injections", inj);
  };

  const removeInjection = (idx) => {
    set("header_injections", (step.header_injections || []).filter((_, i) => i !== idx));
  };

  return (
    <div className="h-full overflow-y-auto p-4 space-y-4">
      <div>
        <Label className="text-xs text-gray-500">Step Name</Label>
        <Input className="mt-1" value={step.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Get Auth Token" />
      </div>

      {/* ── Run Once ─────────────────────────────────────────────── */}
      <div className={`p-3 rounded-lg border space-y-3 ${step.run_once ? "bg-violet-50 border-violet-300" : "bg-[#F8F9FA] border-gray-200"}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wider ${step.run_once ? "text-violet-700" : "text-gray-600"}`}>Run Once</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Execute only on the first record; result shared with all rows via run context</p>
          </div>
          <Switch
            checked={!!step.run_once}
            onCheckedChange={v => set("run_once", v)}
            data-testid={`run-once-toggle-${step.id}`}
          />
        </div>
        {step.run_once && (
          <div className="border-t border-violet-200 pt-3 space-y-2">
            <p className="text-[11px] text-violet-700 bg-violet-100 rounded p-2">
              <strong>Use case:</strong> auth token fetch, tenant lookup, one-time setup call. The <code className="bg-violet-200 px-0.5 rounded">resp_*</code> fields from this step are injected into every subsequent row automatically — no copy-forward needed.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-gray-500">Initial retry limit</Label>
                <Input
                  className="mt-0.5 h-7 text-xs w-full"
                  type="number" min={1} max={10}
                  value={step.run_once_retry_limit ?? 3}
                  onChange={e => set("run_once_retry_limit", parseInt(e.target.value) || 3)}
                  data-testid={`run-once-retry-limit-${step.id}`}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Retries on first execution failure (exp. backoff)</p>
              </div>
              <div className="text-[11px] text-gray-500 pt-3">
                <p className="font-semibold text-gray-600 mb-0.5">Mid-sequence 401/403</p>
                <p>If a later step returns 401 or 403, this step auto-reruns up to the same limit. If the limit is reached, the sequence aborts.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* API Selection */}
      <div className="p-3 bg-[#F8F9FA] rounded-lg border border-gray-200 space-y-3">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">API Definition</p>
        <Select value={step.api_definition_id || "__none__"} onValueChange={v => set("api_definition_id", v === "__none__" ? "" : v)}>
          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select existing API..." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None selected —</SelectItem>
            {apiDefs.map(d => (
              <SelectItem key={d._id} value={d._id}>
                <span className="text-[10px] font-bold mr-1.5 px-1 rounded text-white" style={{ background: METHOD_COLORS[d.method] || "#4B5563" }}>{d.method}</span>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Override mode */}
        <div>
          <Label className="text-xs text-gray-500">Override Mode</Label>
          <div className="grid grid-cols-3 gap-1.5 mt-1">
            {OVERRIDE_MODES.map(m => (
              <button
                key={m.value}
                className={`p-2 rounded border text-left transition-all ${step.api_override_mode === m.value ? "border-[#006080] bg-[#e6f2f5]" : "border-gray-200 bg-white hover:border-gray-300"}`}
                onClick={() => set("api_override_mode", m.value)}
              >
                <div className={`text-xs font-semibold mb-0.5 ${step.api_override_mode === m.value ? "text-[#006080]" : "text-gray-700"}`}>{m.label}</div>
                <div className="text-[10px] text-gray-400 leading-tight">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Delta overrides */}
        {step.api_override_mode === "delta" && (
          <div className="space-y-3 border-t border-gray-200 pt-3">
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
              Delta mode: only overridden fields below replace the base API def. Leave blank to inherit from the original.
            </p>
            <div>
              <Label className="text-xs text-gray-500">Payload Template Override (blank = inherit)</Label>
              <textarea
                className="w-full h-24 mt-1 px-2 py-1.5 text-xs font-mono border border-gray-200 rounded focus:outline-none focus:border-[#006080] resize-none"
                value={step.payload_template_override || ""}
                onChange={e => set("payload_template_override", e.target.value || null)}
                placeholder={'{\n  "key": "{{column}}"\n}'}
              />
            </div>
          </div>
        )}

        {/* Fork: show embedded API def summary */}
        {step.api_override_mode === "fork" && (
          <div className="border-t border-gray-200 pt-3">
            {step.api_def_snapshot ? (
              <div className="bg-green-50 border border-green-200 rounded p-2 text-xs text-green-700">
                ✓ Full copy embedded: <strong>{step.api_def_snapshot.name}</strong>
                <button className="ml-2 text-red-500 hover:underline" onClick={() => set("api_def_snapshot", null)}>Clear fork</button>
              </div>
            ) : selectedApiDef ? (
              <Button size="sm" variant="outline" className="h-7 text-xs w-full" onClick={() => set("api_def_snapshot", { ...selectedApiDef })}>
                <Copy size={11} className="mr-1.5" /> Embed current API def as fork
              </Button>
            ) : (
              <p className="text-xs text-gray-400">Select an API definition above first, then embed as fork.</p>
            )}
          </div>
        )}
      </div>

      {/* Headers with {{resp_*}} support */}
      <div className="p-3 bg-[#F8F9FA] rounded-lg border border-gray-200 space-y-2">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Headers Override</p>
        <p className="text-xs text-gray-400">Use <code className="bg-gray-100 px-1 rounded font-mono">{"{{resp_field}}"}</code> to inject values from previous steps.</p>
        {(step.headers_override || []).map((h, i) => (
          <div key={i} className="flex gap-2">
            <Input className="h-7 text-xs flex-1" value={h.key} onChange={e => { const nh = [...(step.headers_override || [])]; nh[i] = { ...h, key: e.target.value }; set("headers_override", nh); }} placeholder="Header-Name" />
            <Input className="h-7 text-xs flex-1 font-mono" value={h.value} onChange={e => { const nh = [...(step.headers_override || [])]; nh[i] = { ...h, value: e.target.value }; set("headers_override", nh); }} placeholder="value or {{resp_token}}" />
            <button onClick={() => set("headers_override", (step.headers_override || []).filter((_, j) => j !== i))}><X size={12} className="text-gray-400" /></button>
          </div>
        ))}
        <Button size="sm" variant="outline" className="h-6 text-xs w-full" onClick={() => set("headers_override", [...(step.headers_override || []), { key: "", value: "" }])}>
          + Add Header
        </Button>
      </div>

      {/* Header injections */}
      <div className="p-3 bg-[#FEF3C7] rounded-lg border border-amber-200 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">Header Injections</p>
          <Button size="sm" variant="outline" className="h-6 text-xs border-amber-300 text-amber-700" onClick={addHeaderInjection}>+ Add</Button>
        </div>
        <p className="text-[11px] text-amber-700">Automatically inject a <code>resp_*</code> column value from a previous step into a request header. Perfect for auth tokens.</p>
        {(step.header_injections || []).map((inj, i) => (
          <div key={i} className="bg-white rounded border border-amber-200 p-2 space-y-1.5">
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <Label className="text-[10px] text-gray-400">From staging column</Label>
                <Input className="h-6 text-xs mt-0.5 font-mono" value={inj.from_field} onChange={e => updateInjection(i, { from_field: e.target.value })} placeholder="resp_token" />
              </div>
              <div>
                <Label className="text-[10px] text-gray-400">To header name</Label>
                <Input className="h-6 text-xs mt-0.5" value={inj.to_header} onChange={e => updateInjection(i, { to_header: e.target.value })} placeholder="Authorization" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-gray-400">Format <span className="text-gray-300">(use {"{value}"} as placeholder)</span></Label>
              <div className="flex gap-1.5">
                <Input className="h-6 text-xs flex-1 font-mono" value={inj.format} onChange={e => updateInjection(i, { format: e.target.value })} placeholder="Bearer {value}" />
                <select className="h-6 text-[10px] border border-gray-200 rounded px-1" onChange={e => updateInjection(i, { format: e.target.value })} value={inj.format}>
                  <option value="Bearer {value}">Bearer token</option>
                  <option value="ApiKey {value}">ApiKey prefix</option>
                  <option value="{value}">Raw value</option>
                  <option value="Token {value}">Token prefix</option>
                </select>
                <button onClick={() => removeInjection(i)}><X size={11} className="text-gray-400 hover:text-red-500" /></button>
              </div>
            </div>
          </div>
        ))}
        {(step.header_injections || []).length === 0 && (
          <p className="text-[11px] text-amber-600 italic">e.g. Step 1 → POST /auth → resp_token = "eyJ...". Step 2 injects resp_token as "Bearer {"{value}"}" into Authorization header.</p>
        )}
      </div>

      {/* Execution settings */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-gray-500">Concurrency</Label>
          <Input className="mt-1 h-7 text-xs" type="number" min={1} max={10} value={step.concurrency || 1} onChange={e => set("concurrency", parseInt(e.target.value) || 1)} />
        </div>
        <div>
          <Label className="text-xs text-gray-500">Delay (ms)</Label>
          <Input className="mt-1 h-7 text-xs" type="number" min={0} value={step.delay_ms || 0} onChange={e => set("delay_ms", parseInt(e.target.value) || 0)} />
        </div>
      </div>
      <div>
        <Label className="text-xs text-gray-500">Row Filter Expression (optional)</Label>
        <Input className="mt-1 h-7 text-xs font-mono" value={step.row_filter?.expression || ""} onChange={e => set("row_filter", e.target.value ? { expression: e.target.value, action: "skip_if_false" } : null)} placeholder="{status} == 'active'" />
      </div>

      {/* ── Conditional Branching ───────────────────────────────────── */}
      <div className={`p-3 rounded-lg border space-y-3 ${step.condition?.field ? "bg-sky-50 border-sky-300" : "bg-[#F8F9FA] border-gray-200"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <p className={`text-xs font-semibold uppercase tracking-wider ${step.condition?.field ? "text-sky-700" : "text-gray-600"}`}>
              Step Pre-Condition
            </p>
            <div className="relative inline-block group cursor-help ml-0.5">
              <Info size={12} className="text-gray-400 hover:text-sky-600" />
              <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 p-2.5 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none leading-relaxed">
                Evaluated BEFORE this step runs for each row. If the condition fails, apply the selected action: skip this step, skip the whole row, or abort the entire sequence.
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
              </div>
            </div>
          </div>
          <Switch
            checked={!!step.condition?.field}
            onCheckedChange={v => {
              if (v) set("condition", { field: "", operator: "equals", value: "", on_fail: "skip_step" });
              else set("condition", null);
            }}
            data-testid={`condition-toggle-${step.id}`}
          />
        </div>
        {step.condition?.field !== undefined && step.condition !== null && (
          <div className="space-y-2 border-t border-sky-200 pt-3">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[10px] text-gray-500">Field / Column</Label>
                <Input
                  className="mt-0.5 h-7 text-xs font-mono"
                  value={step.condition.field || ""}
                  onChange={e => set("condition", { ...step.condition, field: e.target.value })}
                  placeholder="status"
                  data-testid={`condition-field-${step.id}`}
                />
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Operator</Label>
                <select
                  className="mt-0.5 h-7 w-full text-xs border border-gray-200 rounded px-2 bg-white"
                  value={step.condition.operator || "equals"}
                  onChange={e => set("condition", { ...step.condition, operator: e.target.value })}
                  data-testid={`condition-operator-${step.id}`}
                >
                  {[
                    ["equals", "equals (==)"],
                    ["not_equals", "not equals (!=)"],
                    ["contains", "contains"],
                    ["not_contains", "not contains"],
                    ["gt", "greater than (>)"],
                    ["lt", "less than (<)"],
                    ["is_empty", "is empty"],
                    ["is_not_empty", "is not empty"],
                  ].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] text-gray-500">Value</Label>
                <Input
                  className="mt-0.5 h-7 text-xs"
                  value={step.condition.value || ""}
                  onChange={e => set("condition", { ...step.condition, value: e.target.value })}
                  placeholder="active"
                  disabled={["is_empty", "is_not_empty"].includes(step.condition.operator)}
                  data-testid={`condition-value-${step.id}`}
                />
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-gray-500">If condition FAILS →</Label>
              <div className="flex gap-2 mt-1">
                {[
                  ["skip_step", "Skip step", "Only skip this step"],
                  ["skip_row", "Skip row", "Skip remaining steps for this row"],
                  ["abort_sequence", "Abort", "Stop the entire sequence"],
                ].map(([v, label, hint]) => (
                  <button
                    key={v}
                    className={`flex-1 px-2 py-1.5 rounded border text-xs font-medium transition-all ${step.condition.on_fail === v ? "border-sky-400 bg-sky-100 text-sky-800" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"}`}
                    onClick={() => set("condition", { ...step.condition, on_fail: v })}
                    title={hint}
                    data-testid={`condition-on-fail-${v}-${step.id}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {step.condition.field && (
              <p className="text-[11px] text-sky-600 bg-sky-100 rounded p-1.5">
                Preview: If <code className="bg-sky-200 px-0.5 rounded">{step.condition.field}</code> {step.condition.operator.replace(/_/g, " ")} {!["is_empty", "is_not_empty"].includes(step.condition.operator) && <><code className="bg-sky-200 px-0.5 rounded">"{step.condition.value}"</code> </>}fails → {step.condition.on_fail?.replace(/_/g, " ")}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Version History Panel ────────────────────────────────────────────
function VersionPanel({ seqId, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!seqId) return;
    setLoading(true);
    client.get(`/sequences/${seqId}/versions`).then(r => { setVersions(r.data); setLoading(false); });
  }, [seqId]);

  const restore = async (ver) => {
    if (!window.confirm(`Restore to "${ver.version_name}"?`)) return;
    await client.post(`/sequences/${seqId}/versions/${ver.version_number}/restore`);
    onClose("restored");
  };

  return (
    <div className="space-y-2">
      {loading && <div className="text-center py-4 text-xs text-gray-400">Loading versions...</div>}
      {!loading && versions.length === 0 && <div className="text-center py-4 text-xs text-gray-400">No saved versions yet. Click "Save Version" to create a snapshot.</div>}
      {versions.map(v => (
        <div key={v._id} className="flex items-center gap-3 p-3 bg-[#F8F9FA] rounded-lg border border-gray-200">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-[#006080]">v{v.version_number}</span>
              <span className="text-sm font-semibold text-gray-800">{v.version_name}</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{v.created_at ? new Date(v.created_at).toLocaleString() : "—"}</p>
          </div>
          <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => restore(v)}>
            <RefreshCw size={11} className="mr-1" /> Restore
          </Button>
        </div>
      ))}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────
export default function ExecutionSequencesPage() {
  const [sequences, setSequences] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [apiDefs, setApiDefs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Active sequence editing state
  const [activeSeq, setActiveSeq] = useState(null);  // full sequence object being edited
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [dirty, setDirty] = useState(false);

  // UI modals
  const [showVersionPanel, setShowVersionPanel] = useState(false);
  const [showRunModal, setShowRunModal] = useState(false);
  const [showHistory, setShowHistory] = useState(null);
  const [histories, setHistories] = useState({});
  const [runUploadId, setRunUploadId] = useState("");
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);

  const importRef = useRef();

  useEffect(() => {
    loadAll();
    client.get("/files/").then(r => setUploads(r.data));
    client.get("/api-defs/").then(r => setApiDefs(r.data));
  }, []);

  const loadAll = async () => {
    setLoading(true);
    const res = await client.get("/sequences/").catch(() => ({ data: [] }));
    setSequences(res.data);
    setLoading(false);
  };

  const openSeq = (seq) => {
    setActiveSeq(JSON.parse(JSON.stringify(seq)));
    setSelectedStepId(null);
    setDirty(false);
    setShowVersionPanel(false);
  };

  const newSeq = () => {
    const s = { _id: null, name: "New Sequence", description: "", upload_id: "", steps: [] };
    setActiveSeq(s);
    setSelectedStepId(null);
    setDirty(true);
  };

  const addStep = () => {
    if (!activeSeq) return;
    const order = (activeSeq.steps.length || 0) + 1;
    const step = {
      id: genId(), order, name: `Step ${order}`, step_type: "api",
      api_definition_id: "", api_override_mode: "reference",
      api_def_snapshot: null, payload_template_override: null, headers_override: null,
      header_injections: [], concurrency: 1, delay_ms: 0, row_filter: null,
      run_once: false, run_once_retry_limit: 3, condition: null,
    };
    const newSteps = [...activeSeq.steps, step];
    setActiveSeq(s => ({ ...s, steps: newSteps }));
    setSelectedStepId(step.id);
    setDirty(true);
  };

  const updateStep = (updated) => {
    setActiveSeq(s => {
      let steps = s.steps.map(st => st.id === updated.id ? updated : st);
      // Auto-promote: keep run_once steps sorted before regular steps
      // (preserve relative order within each group)
      const runOnce = steps.filter(st => st.run_once);
      const regular = steps.filter(st => !st.run_once);
      steps = [...runOnce, ...regular].map((st, i) => ({ ...st, order: i + 1 }));
      return { ...s, steps };
    });
    setDirty(true);
  };

  const removeStep = (id) => {
    setActiveSeq(s => ({ ...s, steps: s.steps.filter(st => st.id !== id).map((st, i) => ({ ...st, order: i + 1 })) }));
    if (selectedStepId === id) setSelectedStepId(null);
    setDirty(true);
  };

  const saveSeq = async () => {
    if (!activeSeq) return;
    setSaving(true);
    try {
      let savedSeq;
      if (activeSeq._id) {
        await client.put(`/sequences/${activeSeq._id}`, { name: activeSeq.name, description: activeSeq.description, upload_id: activeSeq.upload_id, steps: activeSeq.steps });
        savedSeq = { ...activeSeq };
      } else {
        const res = await client.post("/sequences/", { name: activeSeq.name, description: activeSeq.description, upload_id: activeSeq.upload_id, steps: activeSeq.steps });
        savedSeq = res.data;
        setActiveSeq(s => ({ ...s, _id: res.data._id }));
      }
      setDirty(false);
      await loadAll();
    } catch (e) { alert(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveVersion = async () => {
    if (!activeSeq?._id) { alert("Save the sequence first"); return; }
    const name = window.prompt("Version name:", `v${Date.now()}`);
    if (!name) return;
    try {
      await client.post(`/sequences/${activeSeq._id}/versions`, { name });
      alert(`Version "${name}" saved!`);
    } catch (e) { alert(formatApiError(e)); }
  };

  const exportSeq = async () => {
    if (!activeSeq?._id) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sequences/${activeSeq._id}/export?include_api_defs=true&include_lookups=true`, "_blank");
  };

  const importSeq = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const text = await file.text();
    const json = JSON.parse(text);
    try {
      const res = await client.post("/sequences/import", json);
      alert(`Imported! ${res.data.created?.sequences?.length || 0} sequence(s), ${res.data.created?.api_definitions?.length || 0} API def(s)`);
      await loadAll();
    } catch (e) { alert(formatApiError(e)); }
    e.target.value = "";
  };

  const runSeq = async () => {
    if (!activeSeq?._id) return;

    // A sequence where every executable step is run_once doesn't need a file.
    const executableSteps = (activeSeq.steps || []).filter(
      s => s.step_type !== "note" && (s.api_definition_id || s.api_def_snapshot)
    );
    const allRunOnce = executableSteps.length > 0 && executableSteps.every(s => s.run_once);

    const uploadId = runUploadId || activeSeq.upload_id;
    if (!uploadId && !allRunOnce) {
      alert("Select a file — this sequence has steps that depend on row data.");
      return;
    }
    setRunning(true);
    try {
      const payload = uploadId ? { upload_id: uploadId } : {};
      const res = await client.post(`/sequences/${activeSeq._id}/run`, payload);
      alert(`Sequence started! Run ID: ${res.data.run_id}`);
      setShowRunModal(false);
    } catch (e) { alert(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const loadHistory = async (seqId) => {
    const res = await client.get(`/sequences/${seqId}/history`).catch(() => ({ data: [] }));
    setHistories(h => ({ ...h, [seqId]: res.data }));
    setShowHistory(showHistory === seqId ? null : seqId);
  };

  const selectedStep = activeSeq?.steps?.find(s => s.id === selectedStepId) || null;
  const statusColor = { completed: "#059669", failed: "#DC2626", running: "#006080", idle: "#9CA3AF" };

  return (
    <div className="flex h-full overflow-hidden">
      {/* ── LEFT: Sequence list ── */}
      <div className="w-64 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
        <div className="px-3 py-3 border-b border-gray-100 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sequences</p>
          <div className="flex gap-1">
            <button className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-[#006080]" onClick={() => importRef.current?.click()} title="Import">
              <Upload size={13} />
            </button>
            <input ref={importRef} type="file" accept=".json" className="hidden" onChange={importSeq} />
            <button className="p-1 hover:bg-[#e6f2f5] rounded text-[#006080]" onClick={newSeq} title="New Sequence">
              <Plus size={14} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          {loading ? <div className="px-3 py-2 text-xs text-gray-400">Loading...</div>
          : sequences.length === 0 ? <div className="px-3 py-2 text-xs text-gray-400">No sequences. Click + to create.</div>
          : sequences.map(seq => (
            <div key={seq._id}>
              <button
                className={`w-full px-3 py-2.5 text-left flex items-start gap-2 hover:bg-gray-50 transition-colors ${activeSeq?._id === seq._id ? "bg-[#e6f2f5] border-l-2 border-[#006080]" : ""}`}
                onClick={() => openSeq(seq)}
              >
                <GitBranch size={14} className="text-[#006080] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{seq.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-[10px] text-gray-400">{seq.steps?.length || 0} steps</span>
                    {seq.last_run_status && (
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: statusColor[seq.last_run_status] || "#9CA3AF" }} />
                    )}
                  </div>
                </div>
              </button>
              {/* Run history inline */}
              <div className="px-3 pb-1 flex gap-1">
                <button className="text-[10px] text-gray-400 hover:text-[#006080]" onClick={() => loadHistory(seq._id)}>history</button>
              </div>
              {showHistory === seq._id && (histories[seq._id] || []).length > 0 && (
                <div className="mx-3 mb-2 bg-gray-50 rounded border border-gray-100 text-[10px] divide-y divide-gray-100">
                  {(histories[seq._id] || []).slice(0, 5).map(r => (
                    <div key={r._id} className="px-2 py-1 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: statusColor[r.status] || "#9CA3AF" }} />
                      <span className="text-gray-500 truncate flex-1">{r.started_at ? new Date(r.started_at).toLocaleString() : "—"}</span>
                      <span className="font-medium" style={{ color: statusColor[r.status] }}>{r.steps_completed}/{r.steps_total}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── CENTER: Sequence designer ── */}
      <div className="flex-1 flex flex-col min-w-0 border-r border-gray-200">
        {!activeSeq ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <GitBranch size={40} className="mx-auto mb-3 text-gray-200" />
              <p>Select a sequence or create a new one</p>
            </div>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="px-4 py-2.5 bg-white border-b border-gray-200 flex items-center gap-2 flex-shrink-0">
              <Input className="h-7 text-sm font-semibold flex-1 max-w-xs border-0 bg-transparent px-0 focus:ring-0" value={activeSeq.name} onChange={e => { setActiveSeq(s => ({ ...s, name: e.target.value })); setDirty(true); }} />
              {dirty && <span className="text-xs text-amber-600">• unsaved</span>}
              <div className="flex-1" />
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setShowVersionPanel(true)} title="Version History">
                <History size={12} className="mr-1" /> Versions
              </Button>
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={saveVersion} title="Save named version">
                <Tag size={12} className="mr-1" /> Save Version
              </Button>
              {activeSeq._id && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={exportSeq}>
                  <Download size={12} className="mr-1" /> Export
                </Button>
              )}
              <Button size="sm" className="h-7 px-3 text-xs bg-[#006080] text-white hover:bg-[#004d66]" onClick={saveSeq} disabled={saving}>
                <Save size={12} className="mr-1" />{saving ? "Saving..." : "Save"}
              </Button>
              <Button size="sm" className="h-7 px-3 text-xs bg-green-600 text-white hover:bg-green-700" onClick={() => setShowRunModal(true)}>
                <Play size={12} className="mr-1" /> Run
              </Button>
            </div>

            {/* Sequence metadata */}
            <div className="px-4 py-2 bg-[#F8F9FA] border-b border-gray-100 flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-gray-400">Default file:</Label>
                <Select value={activeSeq.upload_id || "__none__"} onValueChange={v => { setActiveSeq(s => ({ ...s, upload_id: v === "__none__" ? "" : v })); setDirty(true); }}>
                  <SelectTrigger className="h-6 w-48 text-xs"><SelectValue placeholder="Select at run time" /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Select at run time</SelectItem>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {activeSeq.current_version && <span className="text-xs text-gray-400">Version: {activeSeq.current_version}</span>}
            </div>

            {/* Steps area */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Data flow diagram */}
              {activeSeq.steps.length > 0 && (
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <span className="text-xs text-gray-400">File data</span>
                  {activeSeq.steps.map((step, i) => {
                    const api = apiDefs.find(d => d._id === step.api_definition_id);
                    const hasInjections = (step.header_injections || []).length > 0;
                    return (
                      <React.Fragment key={step.id}>
                        <ArrowRight size={12} className="text-gray-300 flex-shrink-0" />
                        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs cursor-pointer transition-all ${selectedStepId === step.id ? "border-[#006080] bg-[#e6f2f5]" : "border-gray-200 bg-white hover:border-gray-300"}`}
                          onClick={() => setSelectedStepId(step.id)}>
                          <span className="font-bold text-[#006080]">{i + 1}</span>
                          <span className="font-medium text-gray-700 max-w-[100px] truncate">{step.name}</span>
                          {api && <span className="text-[9px] px-1 rounded font-bold text-white" style={{ background: METHOD_COLORS[api.method] || "#4B5563" }}>{api.method}</span>}
                          {hasInjections && <span className="text-[9px] text-amber-600" title="Has header injections">🔑</span>}
                          {step.run_once && <span className="text-[9px] text-violet-600 font-bold" title="Run Once">1×</span>}
                          {step.api_override_mode !== "reference" && <span className="text-[9px] text-purple-600">{step.api_override_mode}</span>}
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <ArrowRight size={12} className="text-gray-300" />
                  <span className="text-xs text-gray-400">Enriched data</span>
                </div>
              )}

              {/* Step cards */}
              <div className="space-y-2">
                {activeSeq.steps.map((step, i) => {
                  const api = apiDefs.find(d => d._id === step.api_definition_id);
                  const hasInjections = (step.header_injections || []).length > 0;
                  const isSelected = selectedStepId === step.id;
                  return (
                    <div
                      key={step.id}
                      className={`rounded-xl border transition-all cursor-pointer ${step.run_once ? "border-l-4 border-l-violet-400" : ""} ${isSelected ? "border-[#006080] shadow-sm" : "border-gray-200 hover:border-gray-300"}`}
                      onClick={() => setSelectedStepId(step.id)}
                    >
                      <div className="flex items-center gap-3 px-4 py-3">
                        <div className={`w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center flex-shrink-0 ${step.run_once ? "bg-violet-500" : "bg-[#006080]"}`}>{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm text-gray-900">{step.name}</span>
                            {step.run_once && (
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-300" title={`Run once · max ${step.run_once_retry_limit ?? 3} re-exec`}>1× RUN ONCE</span>
                            )}
                            {api && (
                              <>
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-bold text-white" style={{ background: METHOD_COLORS[api.method] || "#4B5563" }}>{api.method}</span>
                                <span className="text-xs text-gray-500 font-mono truncate max-w-[160px]">{api.base_url}{api.path}</span>
                              </>
                            )}
                            {!api && step.api_definition_id && <span className="text-xs text-red-500">API def not found</span>}
                            {!step.api_definition_id && <span className="text-xs text-gray-400 italic">No API selected</span>}
                          </div>
                          <div className="flex gap-2 mt-1 flex-wrap">
                            <Badge variant="secondary" className="text-[10px]">{step.api_override_mode}</Badge>
                            {hasInjections && <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700">{step.header_injections.length} header injection(s)</Badge>}
                            {step.run_once && <Badge variant="secondary" className="text-[10px] bg-violet-100 text-violet-600">result shared via run context</Badge>}
                            {!step.run_once && i > 0 && <span className="text-[10px] text-gray-400 italic">Has access to resp_* from steps 1–{i}</span>}
                          </div>
                        </div>
                        <button className="text-red-400 hover:text-red-600 p-1" onClick={e => { e.stopPropagation(); removeStep(step.id); }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button
                  className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-[#006080] hover:text-[#006080] transition-colors flex items-center justify-center gap-2"
                  onClick={addStep}
                >
                  <Plus size={16} /> Add Step
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── RIGHT: Step config panel ── */}
      <div className="w-80 flex-shrink-0 bg-white flex flex-col">
        <div className="px-4 py-2.5 border-b border-gray-200 flex-shrink-0">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {selectedStep ? `Configuring: ${selectedStep.name}` : "Step Configuration"}
          </p>
        </div>
        <div className="flex-1 overflow-hidden">
          <StepConfigPanel step={selectedStep} apiDefs={apiDefs} onUpdate={updateStep} />
        </div>
      </div>

      {/* ── Version history modal ── */}
      {showVersionPanel && activeSeq?._id && (
        <Dialog open onOpenChange={() => setShowVersionPanel(false)}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Version History — {activeSeq.name}</DialogTitle></DialogHeader>
            <VersionPanel seqId={activeSeq._id} onClose={(reason) => {
              setShowVersionPanel(false);
              if (reason === "restored") { loadAll(); }
            }} />
          </DialogContent>
        </Dialog>
      )}

      {/* ── Run modal ── */}
      {showRunModal && (() => {
        const execSteps = (activeSeq?.steps || []).filter(s => s.step_type !== "note" && (s.api_definition_id || s.api_def_snapshot));
        const allRunOnce = execSteps.length > 0 && execSteps.every(s => s.run_once);
        return (
          <Dialog open onOpenChange={() => setShowRunModal(false)}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>Run: {activeSeq?.name}</DialogTitle></DialogHeader>
              <div>
                {allRunOnce ? (
                  <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
                    <p className="text-sm font-semibold text-violet-700 mb-1">No file needed</p>
                    <p className="text-xs text-violet-600">All steps are marked Run Once — the sequence will execute once without iterating over any file.</p>
                  </div>
                ) : (
                  <>
                    <Label className="text-xs text-gray-500">Source File</Label>
                    <Select value={runUploadId || activeSeq?.upload_id || "__none__"} onValueChange={v => setRunUploadId(v === "__none__" ? "" : v)}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Select file..." /></SelectTrigger>
                      <SelectContent><SelectItem value="__none__">Select...</SelectItem>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
                    </Select>
                    <p className="text-xs text-gray-400 mt-2">Each step enriches the file sequentially, passing resp_* columns to the next step.</p>
                  </>
                )}
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setShowRunModal(false)}>Cancel</Button>
                <Button className="bg-[#006080] text-white" onClick={runSeq} disabled={running} data-testid="run-sequence-btn">
                  <Play size={14} className="mr-1.5" />{running ? "Starting..." : "Run Sequence"}
                </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        );
      })()}
    </div>
  );
}
